import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import "./styles/ConfirmModal.css";

export type ConfirmTone = "default" | "danger";

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /** While true, the confirm button shows a loading label and is disabled. */
  isLoading?: boolean;
  /** Disables the confirm button WITHOUT the loading label - e.g. a required
   *  field in `message` that hasn't been filled in yet. Independent of
   *  isLoading: both can gate the same button for different reasons. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  isLoading = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) => {
  // Esc to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, isLoading, onCancel]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="confirm-modal__overlay"
      onClick={() => !isLoading && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        {tone === "danger" && (
          <div className="confirm-modal__icon confirm-modal__icon--danger">
            <AlertTriangle size={22} />
          </div>
        )}
        <h2 id="confirm-modal-title" className="confirm-modal__title">{title}</h2>
        <div className="confirm-modal__message">{message}</div>
        <div className="confirm-modal__actions">
          <button
            type="button"
            className="confirm-modal__cancel"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`confirm-modal__confirm ${tone === "danger" ? "is-danger" : ""}`}
            onClick={onConfirm}
            disabled={isLoading || confirmDisabled}
          >
            {isLoading ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmModal;
